import { c as _c } from "react/compiler-runtime";
import { IcoTrash16 } from '@onefootprint/icons';
import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Button, Form, IconButton, InlineAlert } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { onModEnter } from '@/playbook-builder/utils/on-mod-enter';
import { versionOutputs } from '@/playbook-builder/utils/outputs';
import { versionPrerequisiteIds } from '@/playbook-builder/utils/prerequisites';
import BreakingOutputsAlert from '../../../breaking-outputs-alert/breaking-outputs-alert';
import NewPrerequisitesAlert from '../../../new-prerequisites-alert/new-prerequisites-alert';
type VersionEntry = {
  versionId: string;
  weight: number;
};
type CreateDeploymentFormProps = {
  playbookVersions: PlaybookVersionNoGraph[];
  activeDeploymentVersionIds?: string[];
  activeDeploymentVersions?: VersionEntry[];
  defaultVersions?: VersionEntry[];
  onSuccess: () => void;
  onSameAsActiveChange?: (isSame: boolean) => void;
  onDeploy: (versions: {
    versionId: string;
    weight: number;
  }[]) => Promise<unknown>;
};
type FormData = {
  versions: VersionEntry[];
};
const normalize = (versions: VersionEntry[]) => [...versions].sort((a, b) => a.versionId.localeCompare(b.versionId));
const isSamePolicy = (a: VersionEntry[], b: VersionEntry[]) => {
  if (a.length !== b.length) return false;
  const sortedA = normalize(a);
  const sortedB = normalize(b);
  return sortedA.every((v, i) => v.versionId === sortedB[i].versionId && Number(v.weight) === Number(sortedB[i].weight));
};
const CreateDeploymentForm = t0 => {
  const $ = _c(75);
  const {
    playbookVersions,
    activeDeploymentVersionIds: t1,
    activeDeploymentVersions: t2,
    defaultVersions,
    onSuccess,
    onSameAsActiveChange,
    onDeploy
  } = t0;
  const activeDeploymentVersionIds = t1 === undefined ? [] : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const activeDeploymentVersions = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keyPrefix: "deployment.form"
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    t
  } = useTranslation("playbook-builder", t4);
  let t5;
  if ($[3] !== defaultVersions) {
    t5 = defaultVersions ?? [{
      versionId: "",
      weight: 100
    }];
    $[3] = defaultVersions;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = {
      mode: "onChange",
      defaultValues: {
        versions: t5
      }
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const form = useForm(t6);
  let t7;
  if ($[7] !== form.control) {
    t7 = {
      control: form.control,
      name: "versions"
    };
    $[7] = form.control;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t7);
  let t8;
  if ($[9] !== form.control) {
    t8 = {
      control: form.control,
      name: "versions"
    };
    $[9] = form.control;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const watchedVersions = useWatch(t8);
  const filledVersions = watchedVersions.filter(_temp);
  let t9;
  if ($[11] !== watchedVersions) {
    t9 = watchedVersions.every(_temp2);
    $[11] = watchedVersions;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const allWeightsFilled = t9;
  let t10;
  if ($[13] !== activeDeploymentVersions || $[14] !== watchedVersions) {
    t10 = watchedVersions.every(_temp3) && isSamePolicy(watchedVersions, activeDeploymentVersions);
    $[13] = activeDeploymentVersions;
    $[14] = watchedVersions;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const currentMatchesActive = t10;
  let t11;
  let t12;
  if ($[16] !== currentMatchesActive || $[17] !== onSameAsActiveChange) {
    t11 = () => {
      onSameAsActiveChange?.(currentMatchesActive);
    };
    t12 = [currentMatchesActive, onSameAsActiveChange];
    $[16] = currentMatchesActive;
    $[17] = onSameAsActiveChange;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  const versionIds = filledVersions.map(_temp4);
  let t13;
  if ($[20] !== playbookVersions) {
    t13 = ids => playbookVersions.filter(v_3 => ids.includes(v_3.id));
    $[20] = playbookVersions;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const versionsWithIds = t13;
  const hasDuplicateVersions = new Set(versionIds).size !== versionIds.length;
  let t14;
  if ($[22] !== watchedVersions) {
    t14 = watchedVersions.reduce(_temp5, 0);
    $[22] = watchedVersions;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const totalWeight = t14;
  const weightsDontSum100 = allWeightsFilled && totalWeight !== 100;
  let t15;
  if ($[24] !== form.formState.errors || $[25] !== hasDuplicateVersions || $[26] !== t || $[27] !== weightsDontSum100) {
    t15 = hasDuplicateVersions ? t(_temp6) : weightsDontSum100 ? t(_temp7) : form.formState.errors.root?.message;
    $[24] = form.formState.errors;
    $[25] = hasDuplicateVersions;
    $[26] = t;
    $[27] = weightsDontSum100;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const inlineError = t15;
  const selectableVersions = playbookVersions.filter(_temp8);
  const versionOptions = selectableVersions.map(v_6 => {
    const isInActiveDeployment = activeDeploymentVersionIds.includes(v_6.id);
    return {
      value: v_6.id,
      label: `v${v_6.version}`,
      component: <span className="flex items-center gap-2">{isInActiveDeployment && <span className="size-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden={true} title={t(_temp9)} />}<span>v{v_6.version}</span></span>
    };
  });
  let t16;
  if ($[29] !== playbookVersions) {
    t16 = versionId => {
      const version = playbookVersions.find(v_7 => v_7.id === versionId);
      return version ? `v${version.version}` : versionId;
    };
    $[29] = playbookVersions;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const getVersionLabel = t16;
  let t17;
  if ($[31] !== onDeploy || $[32] !== onSuccess) {
    t17 = async formData => {
      try {
        await onDeploy(formData.versions.map(_temp10));
        onSuccess();
      } catch {}
    };
    $[31] = onDeploy;
    $[32] = onSuccess;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const handleSubmit = t17;
  const submit = form.handleSubmit(handleSubmit);
  const {
    isSubmitting
  } = form.formState;
  const t18 = versionOutputs(versionsWithIds(activeDeploymentVersionIds));
  const t19 = versionOutputs(versionsWithIds(versionIds));
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <BreakingOutputsAlert deployed={t18} pending={t19} />;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t) {
    t21 = t(_temp11);
    $[37] = t;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t21) {
    t22 = <InlineAlert variant="warning">{t21}</InlineAlert>;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== activeDeploymentVersions || $[42] !== getVersionLabel || $[43] !== t) {
    t23 = activeDeploymentVersions.length > 0 && <div className="flex flex-col gap-2"><p className="text-label-3 text-tertiary">{t(_temp12)}</p>{activeDeploymentVersions.map(v_9 => <div key={v_9.versionId} className="flex items-center gap-3 p-3 rounded-lg bg-secondary"><span className="text-label-3 text-primary bg-primary border border-tertiary rounded px-2 py-0.5 shrink-0">{getVersionLabel(v_9.versionId)}</span><div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-accent" style={{
            width: `${v_9.weight}%`
          }} /></div><span className="text-label-2 text-primary w-12 text-right shrink-0">{v_9.weight}%</span></div>)}</div>;
    $[41] = activeDeploymentVersions;
    $[42] = getVersionLabel;
    $[43] = t;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  const t24 = "create-deployment-form";
  const t25 = onModEnter(() => {
    if (!currentMatchesActive && !isSubmitting) {
      submit();
    }
  });
  let t26;
  if ($[45] !== t) {
    t26 = t(_temp13);
    $[45] = t;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t26) {
    t27 = <p className="text-label-2 text-tertiary mb-1">{t26}</p>;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== inlineError) {
    t28 = inlineError && <InlineAlert variant="error">{inlineError}</InlineAlert>;
    $[49] = inlineError;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== fields || $[52] !== remove || $[53] !== t || $[54] !== versionOptions) {
    let t30;
    if ($[56] !== fields.length || $[57] !== remove || $[58] !== t || $[59] !== versionOptions) {
      t30 = (field, index) => <div key={field.id} className="flex gap-3 items-start"><div className="flex-1"><Form.Select size="compact" name={`versions.${index}.versionId`} aria-label={t(_temp14)} options={versionOptions} placeholder={t(_temp15)} rules={{
            required: t(_temp16)
          }} /></div><div className="w-28"><Form.Input size="compact" name={`versions.${index}.weight`} aria-label={t(_temp17)} type="number" placeholder={t(_temp18)} rules={{
            required: t(_temp19),
            min: {
              value: 0,
              message: t(_temp20)
            },
            max: {
              value: 100,
              message: t(_temp21)
            }
          }} /></div>{fields.length > 1 && <IconButton aria-label={t(_temp22)} icon={IcoTrash16} size="compact" onClick={() => remove(index)} />}</div>;
      $[56] = fields.length;
      $[57] = remove;
      $[58] = t;
      $[59] = versionOptions;
      $[60] = t30;
    } else {
      t30 = $[60];
    }
    t29 = fields.map(t30);
    $[51] = fields;
    $[52] = remove;
    $[53] = t;
    $[54] = versionOptions;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[61] !== append) {
    t30 = () => append({
      versionId: "",
      weight: 0
    });
    $[61] = append;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t) {
    t31 = t(_temp23);
    $[63] = t;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t30 || $[66] !== t31) {
    t32 = <div className="mt-2"><Button size="compact" variant="secondary" type="button" onClick={t30}>{t31}</Button></div>;
    $[65] = t30;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== submit || $[69] !== t25 || $[70] !== t27 || $[71] !== t28 || $[72] !== t29 || $[73] !== t32) {
    t33 = <form id={t24} onSubmit={submit} onKeyDownCapture={t25} className="flex flex-col gap-2">{t27}{t28}{t29}{t32}</form>;
    $[68] = submit;
    $[69] = t25;
    $[70] = t27;
    $[71] = t28;
    $[72] = t29;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  return <FormProvider {...form}><div className="flex flex-col gap-6"><div className="flex flex-col gap-3"><NewPrerequisitesAlert deployed={versionPrerequisiteIds(versionsWithIds(activeDeploymentVersionIds))} pending={versionPrerequisiteIds(versionsWithIds(versionIds))} />{t20}{t22}</div>{t23}{t33}</div></FormProvider>;
};
export default CreateDeploymentForm;
function _temp(v) {
  return v.versionId !== "";
}
function _temp2(v_0) {
  return String(v_0.weight) !== "";
}
function _temp3(v_1) {
  return v_1.versionId !== "";
}
function _temp4(v_2) {
  return v_2.versionId;
}
function _temp5(sum, v_4) {
  return sum + Number(v_4.weight);
}
function _temp6($) {
  return $.errors.duplicateVersion;
}
function _temp7($_0) {
  return $_0.errors.weightsMustSum100;
}
function _temp8(v_5) {
  return v_5.isExecutable;
}
function _temp9($_1) {
  return $_1.partOfActiveDeployment;
}
function _temp10(v_8) {
  return {
    versionId: v_8.versionId,
    weight: Number(v_8.weight)
  };
}
function _temp11($_2) {
  return $_2.deployConfirmation.description;
}
function _temp12($_3) {
  return $_3.currentLabel;
}
function _temp13($_4) {
  return $_4.newLabel;
}
function _temp14($_5) {
  return $_5.version.label;
}
function _temp15($_6) {
  return $_6.version.placeholder;
}
function _temp16($_7) {
  return $_7.version.required;
}
function _temp17($_8) {
  return $_8.weight.label;
}
function _temp18($_9) {
  return $_9.weight.placeholder;
}
function _temp19($_10) {
  return $_10.weight.required;
}
function _temp20($_11) {
  return $_11.weight.min;
}
function _temp21($_12) {
  return $_12.weight.max;
}
function _temp22($_13) {
  return $_13.removeVersion;
}
function _temp23($_14) {
  return $_14.addVersion;
}